<template>
  <div>
    <app-cells><h1 class="title">Flights selection</h1></app-cells>
    <div class="flights-selection">
      <form @submit.prevent="onCheckForm">
        <div class="flights-selection__row">
          <app-form-group label="Date" required>
            <app-date-picker
              @input="onDateChanged"
              v-model="form.date"
              placeholder="Date"
              :error="$v.form.date.$error"
            />
            <template #error>
              <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
              <div v-if="$v.form.date.$dirty && !$v.form.date.minValue">
                Date cannot be less than the current date
              </div>
            </template>
          </app-form-group>
          <app-form-group label="Origin" class="form-group--no-indent" required>
            <app-select
              @input="onOriginChange"
              v-model="form.flight_from"
              :options="flight_from_options"
              :reduce="item => item.id"
              :clearable="false"
              :readonly="!form.date"
              placeholder="Choose origin"
              :error="$v.form.flight_from.$error"
            />
            <template #error>
              <div v-if="$v.form.flight_from.$dirty && !$v.form.flight_from.required">Required field</div>
            </template>
          </app-form-group>
          <app-form-group label="Destination" class="form-group--no-indent" required>
            <app-select
              @input="onDestinationChange"
              v-model="form.flight_to"
              :options="flight_to_options"
              :reduce="item => item.id"
              :clearable="false"
              :readonly="!form.flight_from"
              placeholder="Choose destination"
              :error="$v.form.flight_to.$error"
            />
            <template #error>
              <div v-if="$v.form.flight_to.$dirty && !$v.form.flight_to.required">Required field</div>
            </template>
          </app-form-group>
          <app-form-group label="Airline" class="form-group--no-indent">
            <app-select
              v-model="form.airline"
              :options="airline_options"
              :reduce="item => item.id"
              :clearable="false"
              :readonly="!form.flight_to"
              placeholder="Choose airline"
            />
          </app-form-group>
        </div>
        <app-cells :indent="false" position="end">
          <app-button @click.native="onFormReset" theme="dark">Reset</app-button>
          <app-button type="submit" ref="submit">Search</app-button>
        </app-cells>
      </form>
      <app-cells><h2 class="title">Search result</h2></app-cells>
      <div v-if="search_result.length" class="flights-selection__result">
        <div v-for="item in search_result" :key="`item_${item.id}`" class="flights-selection__item">
          <h3 class="title">{{ item.airline.name }} ({{ item.departure }} -> {{ item.arrival }})</h3>
          <app-luggage-card
            v-for="card in item.luggage_options"
            :key="`card_${card.id}`"
            :age_type="card.age_type"
            :weight="card.weight"
            :price="card.price"
            :clickable="false"
          />
        </div>
      </div>
      <div v-else class="no-results no-results--margin">
        <img src="@/assets/img/schedule-plug-icon.svg" alt="">
        <div class="no-results__title">No Results</div>
        <div class="no-results__text">There’s no flights yet. Set or change request and try again</div>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import {
  getDomesticFlightsSchedulesAirlinesAvailable,
  getDomesticFlightsSchedulesDestinationAvailable,
  getDomesticFlightsSchedulesOriginAvailable,
  getDomesticFlightsSchedulesScheduleSelection,
} from '@/http'
import AppLuggageCard from '@/components/AppLuggageCard.vue'

export default {
  name: 'FlightsSelection',
  components: { AppLuggageCard },
  data() {
    return {
      form: {},
      flight_from_options: [],
      flight_to_options: [],
      airline_options: [],
      search_result: [],
    }
  },
  validations: {
    form: {
      date: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        },
      },
      flight_from: { required },
      flight_to: { required },
    }
  },
  watch: {
    form: {
      handler() {
        if (this.search_result.length) this.search_result = []
      },
      deep: true
    }
  },
  methods: {
    onDateChanged() {
      delete this.form.flight_from
      delete this.form.flight_to
      delete this.form.airline
      getDomesticFlightsSchedulesOriginAvailable({ date: this.form.date })
        .then(res => { this.flight_from_options = res.data })
    },
    onOriginChange() {
      delete this.form.flight_to
      delete this.form.airline
      getDomesticFlightsSchedulesDestinationAvailable({
        date: this.form.date,
        flight_from: this.form.flight_from
      })
        .then(res => { this.flight_to_options = res.data })
    },
    onDestinationChange() {
      delete this.form.airline
      getDomesticFlightsSchedulesAirlinesAvailable({
        date: this.form.date,
        flight_from: this.form.flight_from,
        flight_to: this.form.flight_to,
      })
        .then(res => { this.airline_options = res.data })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      getDomesticFlightsSchedulesScheduleSelection({
        date: this.form.date,
        flight_from: this.form.flight_from,
        flight_to: this.form.flight_to,
        airline: this.form.airline,
      })
        .finally(() => { this.$refs.submit.preload = false })
        .then(res => { this.search_result = res.data })
    },
    onFormReset() {
      this.form = {}
      this.$v.form.$reset()
      this.search_result = []
    }
  }
}
</script>
<style lang="sass" scoped>
.flights-selection__row
  display: grid
  grid-template-columns: 200px repeat(3, 250px)
  gap: 20px

.flights-selection__result
  max-width: 600px

.flights-selection__item
  margin-bottom: 20px

  .title
    margin-bottom: 10px
</style>
